import { Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SectionHeader } from './SectionHeader';

interface TimelineEvent {
  title: string;
  date?: string;
}

interface TrackingTimelineProps {
  currentStatusSlug: string;
  timelineEvents?: TimelineEvent[];
}

interface DeliveryStep {
  key: string;
  labelKey: string;
}

const FULL_DELIVERY_PATH: DeliveryStep[] = [
  { key: 'new', labelKey: 'order_created' },
  { key: 'scheduled', labelKey: 'scheduled' },
  { key: 'collecting', labelKey: 'ready_for_pickup' },
  { key: 'collected', labelKey: 'picked_up' },
  { key: 'en-route', labelKey: 'in_transit' },
  { key: 'out-for-delivery', labelKey: 'out_for_delivery_step' },
  { key: 'delivered', labelKey: 'delivered' },
];

/**
 * O-9: Maps ALL backend status slugs to their position in FULL_DELIVERY_PATH.
 * Statuses not directly in the path (in-transit, on-hold, exception, RTO etc.)
 * are mapped to the last step they should show as active — the timeline should
 * NEVER go backwards. "Always keep the last status on."
 */
const STATUS_TO_STEP_INDEX: Record<string, number> = {
  // Direct matches
  'new': 0,
  'scheduled': 1,
  'collecting': 2,
  'out-for-collecting': 2,
  'collected': 3,
  'sorted': 3,
  'transferred-carrier': 3,
  // In transit / en-route — step 4
  'en-route': 4,
  'in-transit': 4,
  'received-warehouse': 4,
  'received-carrier-warehouse': 4,
  // Out for delivery — step 5
  'out-for-delivery': 5,
  'out-for-collection': 5,
  'postponed': 5,
  // On-hold / exception — keep at last known position (in-transit level)
  'on-hold': 4,
  'hold-resolved': 4,
  'retry-request': 4,
  'exception': 4,
  // Delivered — step 6
  'delivered': 6,
  'partially-delivered': 6,
  // RTO statuses — order was at least out-for-delivery before RTO
  'rto-requested': 5,
  'rto-request-pending': 5,
  'rto-scheduled': 5,
  'rto-collecting': 5,
  'rto-in-transit': 5,
  'rto-drop-off-scanned': 5,
  'rto-received-warehouse': 5,
  'rto-received-carrier-warehouse': 5,
  'rto-partially-delivered': 6,
  'rto-delivered': 6,
  'return-scheduled': 5,
  'returning-to-origin': 5,
  'returned-to-warehouse': 5,
  // Inventory / resend
  'inventory': 6,
  'resend': 0,
};

/**
 * RTO (return-to-merchant) orders travel a DIFFERENT path than the forward
 * delivery above. Folding RTO statuses onto FULL_DELIVERY_PATH made a returned
 * order read as "Delivered (In progress)" — contradicting the "Returned" header.
 * When the order is RTO we render this return-journey path instead: the delivery
 * was attempted, then the parcel is requested back, returns to origin, and is
 * handed back to the merchant. Labels reuse the existing merchant RTO strings.
 */
const RTO_DELIVERY_PATH: DeliveryStep[] = [
  { key: 'new', labelKey: 'order_created' },
  { key: 'out-for-delivery', labelKey: 'out_for_delivery' },
  { key: 'rto-requested', labelKey: 'rto_request' },
  { key: 'rto-in-transit', labelKey: 'rto_in_transit' },
  { key: 'rto-delivered', labelKey: 'returned' },
];

/** Maps RTO / return status slugs onto RTO_DELIVERY_PATH positions. */
const RTO_STATUS_TO_STEP_INDEX: Record<string, number> = {
  'new': 0,
  'resend': 0,
  // Delivery attempt that preceded the return — step 1
  'out-for-delivery': 1,
  'out-for-collection': 1,
  'postponed': 1,
  // Return requested — step 2
  'rto-requested': 2,
  'rto-request-pending': 2,
  'rto-scheduled': 2,
  'return-scheduled': 2,
  // Returning to origin — step 3
  'rto-collecting': 3,
  'rto-in-transit': 3,
  'rto-drop-off-scanned': 3,
  'rto-received-warehouse': 3,
  'rto-received-carrier-warehouse': 3,
  'returning-to-origin': 3,
  'returned-to-warehouse': 3,
  // Returned to merchant — step 4
  'rto-delivered': 4,
  'rto-partially-delivered': 4,
};

/** An RTO/return order takes the return path, not the forward-delivery path. */
function isRtoStatus(slug: string): boolean {
  return /^(rto-|return)/.test(slug);
}

type StepState = 'done' | 'active' | 'pending';

function getStepState(stepIndex: number, currentIndex: number): StepState {
  if (stepIndex < currentIndex) return 'done';
  if (stepIndex === currentIndex) return 'active';
  return 'pending';
}

export function TrackingTimeline({ currentStatusSlug, timelineEvents = [] }: TrackingTimelineProps) {
  const { t } = useTranslation();

  // RTO orders follow the return path; everything else the forward path.
  const isRto = isRtoStatus(currentStatusSlug);
  const path = isRto ? RTO_DELIVERY_PATH : FULL_DELIVERY_PATH;
  const stepIndexMap = isRto ? RTO_STATUS_TO_STEP_INDEX : STATUS_TO_STEP_INDEX;

  // Use explicit mapping instead of findIndex — handles all statuses. Unknown
  // RTO slugs default to "RTO Requested" (the order is at least being returned);
  // unknown forward slugs default to "Order Created".
  const resolvedCurrentIndex = stepIndexMap[currentStatusSlug] ?? (isRto ? 2 : 0);

  // Reversed so the newest step is at top, oldest at bottom.
  const reversedPath = [...path].reverse();
  // Map reversed path index → original path index
  const originalIndexOf = (reversedIndex: number) => path.length - 1 - reversedIndex;

  return (
    <div className="rounded-2xl border border-sd-border bg-sd-card p-5">
      <SectionHeader icon={Truck} title={String(t('tracking_timeline'))} />

      <div>
        {reversedPath.map((step, i) => {
          const originalIndex = originalIndexOf(i);
          const state = getStepState(originalIndex, resolvedCurrentIndex);

          // The next item in the reversed list is the step below us visually,
          // which corresponds to the step with originalIndex - 1 (one earlier).
          const nextOriginalIndex = originalIndexOf(i + 1);
          const nextState =
            i < reversedPath.length - 1
              ? getStepState(nextOriginalIndex, resolvedCurrentIndex)
              : 'pending';

          // Match a timeline event to this step by checking if its title
          // contains the step label (case-insensitive), or fall back by index
          const matchedEvent = timelineEvents.find((ev) =>
            ev.title?.toLowerCase().includes(step.key.replace('-', ' ')),
          );

          const connectorActive = nextState === 'done' || nextState === 'active';

          return (
            <div key={step.key} className="relative pb-5 last:pb-0">
              {/* Connector line (except last item) */}
              {i < reversedPath.length - 1 && (
                <div
                  className={`absolute start-[11px] top-[22px] w-0.5 h-full ${
                    connectorActive ? 'bg-sd-primary' : 'bg-sd-border'
                  }`}
                />
              )}

              <div className="flex items-start gap-3">
                {/* Circle node */}
                <div
                  className={`shrink-0 mt-0.5 h-[22px] w-[22px] rounded-full border-2 flex items-center justify-center z-10 ${
                    state === 'done'
                      ? 'border-sd-primary bg-sd-primary'
                      : state === 'active'
                        ? 'border-sd-primary bg-sd-card'
                        : 'border-[hsl(40,10%,80%)] bg-[hsl(40,10%,96%)]'
                  }`}
                >
                  {state === 'done' && (
                    <svg
                      className="w-3 h-3 text-sd-primary-foreground"
                      viewBox="0 0 12 12"
                      fill="none"
                    >
                      <path
                        d="M2 6l3 3 5-5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  {state === 'active' && (
                    <div className="h-2.5 w-2.5 rounded-full bg-sd-primary animate-pulse" />
                  )}
                  {state === 'pending' && (
                    <div className="h-2 w-2 rounded-full bg-[hsl(40,10%,80%)]" />
                  )}
                </div>

                {/* Label and date */}
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-[13px] font-semibold leading-tight m-0 ${
                      state === 'pending' ? 'text-sd-muted-foreground' : 'text-sd-foreground'
                    }`}
                  >
                    {String(t(step.labelKey))}
                  </p>
                  {state !== 'pending' && (
                    <p className="text-[11px] text-sd-muted-foreground mt-0.5 m-0">
                      {matchedEvent?.date ?? (state === 'active' ? String(t('in_progress_step')) : '')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
